<template>
  <DpplHeaderFooterContent class="flsa-submission-history right-sidebar h-100">
    <template #header>
      <div class="px-6 py-4 border-b-sm">
        <h2 class="text-h2">
          Submission history
        </h2>
      </div>
    </template>

    <div class="flsa-submission-history-body">
      <v-sheet
        v-if="listLoading"
        class="d-flex flex-column justify-center"
        min-height="200px"
      >
        <div class="mx-auto">
          <v-progress-circular
            :size="40"
            color="primary"
            indeterminate
          />
        </div>
      </v-sheet>

      <template v-else>
        <div
          v-if="!submissions.length"
          class="px-6 py-4 text-body-2 text-textLight"
        >
          No submissions yet for this form and job profile.
        </div>

        <FlsaSubmissionTimeline
          v-else
          :submissions="submissions"
          :current-submission-ulid="currentSubmissionUlid"
          show-current-version-badge
          @submission-click="handleItemClick"
        />
      </template>
    </div>
  </DpplHeaderFooterContent>
</template>

<script setup>
import {
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import FlsaSubmissionTimeline from '@/components/jobProfiles/editor/FlsaSubmissionTimeline.vue';
import { getFlsaForms, getFlsaSubmissions } from '@/api/jobProfile';
import { attachFormYears } from '@/helpers/flsa';
import { useAppSnackbar } from '@/stores/app/snackbar';

const props = defineProps({
  flsaFormUlid: {
    type: String,
    required: true,
  },
  jobProfileUlid: {
    type: String,
    default: null,
  },
  draftJobProfileUlid: {
    type: String,
    default: null,
  },
  currentSubmissionUlid: {
    type: String,
    default: null,
  },
});

const router = useRouter();

const submissions = ref([]);
const listLoading = ref(false);

const loadSubmissions = async () => {
  if (!props.jobProfileUlid && !props.draftJobProfileUlid) {
    submissions.value = [];

    return;
  }

  listLoading.value = true;

  try {
    const [formsData, subsData] = await Promise.all([
      getFlsaForms(),
      getFlsaSubmissions({
        flsaFormUlid: props.flsaFormUlid,
        jobProfileUlid: props.jobProfileUlid || undefined,
        draftJobProfileUlid: props.draftJobProfileUlid || undefined,
      }),
    ]);

    submissions.value = attachFormYears(subsData.items, formsData);
  } catch (error) {
    submissions.value = [];

    useAppSnackbar().addErrorSnackbar({
      text: `Failed to load submission history — ${error.message}`,
    });
  } finally {
    listLoading.value = false;
  }
};

const handleItemClick = (item) => {
  if (props.currentSubmissionUlid && item.ulid === props.currentSubmissionUlid) return;

  router.push({ name: 'flsa-form-view', params: { submissionUlid: item.ulid } });
};

onMounted(() => {
  loadSubmissions();
});

watch(
  () => [
    props.flsaFormUlid,
    props.jobProfileUlid,
    props.draftJobProfileUlid,
  ],
  () => {
    loadSubmissions();
  },
);
</script>

<style scoped lang="less">
.flsa-submission-history {
  width: 100%;
  max-width: 100%;
}

.flsa-submission-history-body {
  min-height: 0;
  width: 100%;
}
</style>
